import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { useUpdateEffect } from 'ahooks';
import { Drawer as ADrawer, Select, SelectProps } from 'antd';
import { assign, first, isEmpty, isNil, values } from 'lodash-es';
import { useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { Section } from 'services/api/dashboard';
import { ctx2FetchParam, percentDisplay } from '../../utils';
import { useDashboardFilter } from '../Filter/useFilter';
import EchartsGroup from './EchartsGroup';
import Header from './Header';
import { useIndustryRank, useRecoverRank } from './hooks/api';
import useDrawerContext from './hooks/useDrawer';
import { translateSection } from './utils';

const Drawer = styled(ADrawer)`
  top: 174px;
  .ant-drawer-body {
    padding: 18px 20px;
    overflow: auto;
    flex: 1 0 calc(100vh - 174px - 55px + 16px);
  }
  .ant-drawer-wrapper-body {
    height: initial;
  }
  .ant-drawer-content-wrapper {
    width: 53.05% !important;
    min-width: 801px;
  }
`;

const Selection = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  color: #515359;
  background: #f7f9fa;
  margin: 8px 0 13px 0;
  height: 48px;
`;
/**
 * 拼接行业片段
 * @param section
 * @returns "" || section.join("/")
 */
const concatSection = (section?: Section) => {
  if (isNil(section)) {
    return '';
  }
  return values(section)?.join('/');
};
const ALL_INDUSTRY_FIELD = '所有平台/所有类型/所有地区';
function TrendDrawer() {
  const { platform, storeType, storeInfo, department, dateRange } = useDashboardFilter();
  const { visible, type, params, setTrendValue } = useDrawerContext();
  const { cancelType, performanceName, reason, isCod } = params;
  const [filter, setFilter] = useState<string | null>(null);
  const { t } = useTranslate();
  const commonRequestParams = useMemo(() => {
    return assign(
      {},
      ctx2FetchParam({
        platform,
        storeType,
        storeInfo,
        dateRange,
      }),
      {
        regions: storeInfo?.region,
        departmentId: department?.id,
      },
    );
  }, [platform, storeType, storeInfo, dateRange, department]);
  const {
    loading: industryLoading,
    data: industryData,
    run: industryRun,
  } = useIndustryRank({
    ...commonRequestParams,
    performanceName,
  });
  const {
    loading: recoverLoading,
    data: recoverData,
    runAsync: recoverRun,
  } = useRecoverRank({
    ...commonRequestParams,
    reason,
    cancelType,
    isCod,
  });
  const isRank = type === 'rank';
  const data = isRank ? industryData : recoverData;
  const loading = isRank ? industryLoading : recoverLoading;
  useUpdateEffect(() => {
    if (visible && type === 'rank') {
      industryRun();
    }
  }, [visible, commonRequestParams, performanceName]);
  useUpdateEffect(() => {
    if (visible && type === 'recover') {
      recoverRun();
    }
  }, [visible, commonRequestParams, cancelType, isCod, reason]);
  /**
   * 默认设置选择框为第一条数据
   */
  useUpdateEffect(() => {
    if (isEmpty(data?.rankOfSections)) {
      setFilter('');
      return;
    }
    setFilter(concatSection(data?.rankOfSections?.[0]?.section));
  }, [data?.rankOfSections]);
  /**
   * 根据选择框筛选数据源
   */
  const filterData = useMemo(() => {
    if (isNil(filter)) {
      return first(data?.rankOfSections);
    }
    return data?.rankOfSections?.filter((r) => {
      return concatSection(r.section) === filter;
    })?.[0];
  }, [data?.rankOfSections, filter]);
  /**
   * 不参与计算排名显示逻辑
   * @return true 为不参与
   * @return false 为参与
   */
  const unknownRank = isNil(filterData) ? false : !filterData.visible;

  const options = useMemo(() => {
    const options: SelectProps['options'] =
      data?.rankOfSections?.map((r) => {
        return {
          label: translateSection(r?.section, t),
          value: concatSection(r.section),
        };
      }) ?? [];
    return options;
  }, [data?.rankOfSections, t]);
  const all = filter === ALL_INDUSTRY_FIELD || isEmpty(filter);
  const percent = (() => {
    const beatRatio = all
      ? data?.overallBeatRatio
      : data?.rankOfSections.filter((r) => concatSection(r.section) === filter)?.[0]?.beatRatio;
    if (isNil(beatRatio) || beatRatio < 0) {
      return '-';
    }
    return percentDisplay(beatRatio);
  })();

  const industryLength = (() => {
    return `${
      data?.rankOfSections?.filter((r) => concatSection(r.section) !== ALL_INDUSTRY_FIELD)
        ?.length || '-'
    }`;
  })();
  return (
    <Drawer
      visible={visible}
      mask={false}
      title={t('ViewDashboard.行业排名天级趋势')}
      onClose={() => {
        setTrendValue({
          visible: false,
        });
      }}
    >
      <Header
        params={params}
        platform={platform}
        percent={percent}
        industryLength={industryLength}
        all={all}
      />
      <Selection>
        <div
          className={css`
            padding: 0 6px 0 16px;
          `}
        >
          {t('ViewDashboard.选择行业')}
        </div>
        <Select
          className={css`
            width: 49.8%;
          `}
          onChange={(value: string) => {
            setFilter(value);
          }}
          value={filter}
          options={options}
        />
      </Selection>
      <EchartsGroup
        loading={loading}
        data={filterData}
        unknownRank={unknownRank}
        performanceName={performanceName}
        title="我的指标"
      />
      <EchartsGroup
        loading={loading}
        data={filterData}
        unknownRank={unknownRank}
        title="行业参考指标"
      />
    </Drawer>
  );
}
export default TrendDrawer;
